import { Skin } from './Application';
import { Hook, Sandbox } from './Sandbox';
import { Connector } from './Connector';

import { capitalize } from './utils/string';
import { saveStateToHash, readStateFromHash } from 'lib/util/url';

declare global {
	interface Window {
		debug : Console
	}
}

interface Action<D> {
	(data : D | {}) : void
}

interface SkinConstructor<M extends Module, S extends M> {
	new(parent : M) : S
}

interface Logger {
	log(message? : any, ...optionalParams : any[]) : void;
	error(message? : any, ...optionalParams : any[]) : void;
	warn(message? : any, ...optionalParams : any[]) : void;
	info(message? : any, ...optionalParams : any[]) : void;
	debug(message? : any, ...optionalParams : any[]) : void;
}

export class Module {
	/**
	 * Contains the module context.
	 *
	 * @property $ctx
	 * @type jQuery
	 */
	public $ctx : JQuery;

	/**
	 * Contains the unique module ID.
	 *
	 * @property id
	 * @type number
	 */
	public id : number;


	/**
	 * Contains the module name
	 */
	public modName : string;

	/**
	 * Contains the unique url hash key for deeplinking of this module's state.
	 * This can be set by the module but will be made unique by the Application class.
	 */
	public hashKey : string;

	/**
	 * Contains the attached connectors.
	 *
	 * @property connectors
	 * @type Object
	 */
	public connectors : {[name : string] : Connector};

	/**
	 * The sandbox to get the resources from.
	 *
	 * @property sandbox
	 * @type Sandbox
	 */
	public sandbox : Sandbox;

	public log! : Logger & typeof window.console.log;
	public logInternal : Logger & typeof window.console.log;

	/**
	 * Whether debug mode is on.
	 */
	protected debug! : boolean;
	private debugInternal : boolean;

	constructor($ctx : JQuery, sandbox : Sandbox, id : number, modName : string) {
		this.$ctx = $ctx;
		this.sandbox = sandbox;
		this.id = id;
		this.modName = modName;
		this.hashKey = modName;

		this.connectors = {};

		this.debugInternal = !!sandbox.config.debug;
		if(!('debug' in (this as any))) {
			this.debug = this.debugInternal;
		}

		/**
		 * Shortcut to debug.log, prepends the module name to the output
		 */
		// Added by Swisscom (Raphael Schweikert, taascraa)
		const console : Console = window.debug || window.console;
		const log = (level : keyof Console, ...args : any[]) => {
			if(!this.debugInternal) {
				return;
			}
			console[level].call(console, `Tc.Module.${this.modName} >`, ...args);
		};
		this.logInternal = log.bind(this, 'log') as any;
		const levels : (keyof Logger)[] = ['error', 'warn', 'info', 'debug', 'log'];
		for(const level of levels) {
			this.logInternal[level] = log.bind(this, level);
		}
		if(!('log' in (this as any))) {
			this.log = this.logInternal;
		}
	}

	public on?(callback : Hook) : void;
	public after?(hashState : Record<string, any>) : void;
	public moduleStateChanged?(hashState : Record<string, any>) : void;

	/**
	 * Template method to start (i.e. init) the module.
	 * This method provides hook functions which can be overridden
	 * by the individual instance.
	 *
	 * @method start
	 */
	public start() {
		// Call the hook method from the individual instance and provide the appropriate callback
		// Modified by Swisscom (Raphael Schweikert, taascraa): Warn if callback not called after 300ms
		if (this.on) {
			let timeout : number | null = null;

			if(this.debugInternal) {
				timeout = window.setTimeout(() => {
					this.logInternal.error('Module’s `on` hasn’t called its callback after 300ms');
				}, 300);
			}
			const callAfter = () => {
				if(timeout) {
					window.clearTimeout(timeout);
				}
				this.initAfter();
			};
			this.on(callAfter);
		} else {
			this.logInternal.error('Module doesn’t implement `on`');
		}
	}

	/**
	 * Template method to stop the module.
	 *
	 * @method stop
	 */
	public stop() {
		const $ctx = this.$ctx;

		// Remove all bound events and associated jQuery data
		$('*', $ctx).off().removeData();
		$ctx.off().removeData();
	}

	/**
	 * Initialization callback.
	 *
	 * @method initAfter
	 * @protected
	 */
	public initAfter() {
		this.sandbox.ready(() => {
			/*
			 * Call the 'after' hook method from the individual instance
			 */
			if (this.after) {
				let hashState : Record<string, any> = {};
				if(this.moduleStateChanged) {
					hashState = readStateFromHash(this.hashKey, true)!;
				}
				this.after(hashState);
			}
		});
	}


	/**
	 * Notifies all attached connectors about changes.
	 *
	 * @method fire
	 * @param {String} state The new state
	 * @param {Object} data The data to provide to your connected modules (optional)
	 * @param {Array} channels  A list containing the channel ids to send the event to (optional)
	 * @param {Function} defaultAction The default action to perform (optional)
	 */
	public fire<D>(state : string, defaultAction? : Action<D>) : void;
	public fire<D>(state : string, data : D, defaultAction? : Action<D>) : void;
	public fire<D>(state : string, data : D, channels : string[], defaultAction? : Action<D>) : void;
	public fire<D>(
		state : string,
		data? : D | Action<D>,
		channels? : string[] | Action<D>,
		defaultAction? : Action<D>,
	) {
		const connectors = this.connectors;
		let shouldBeCalled = true;  // Indicates whether the default handler should be called

		// Validate params
		if (!channels && !defaultAction) {
			// Max. 2 params
			if (typeof data === 'function') {
				// (state, defaultAction)
				defaultAction = data as Action<D>;
				data = undefined;
			} else if (Array.isArray(data)) {
				// (state, channels)
				channels = data;
				data = undefined;
			}
		} else if (!defaultAction) {
			// 2-3 params
			if (typeof channels === 'function') {
				// (state, data, defaultAction)
				defaultAction = channels;
				channels = undefined;
			}

			if (Array.isArray(data)) {
				// (state, channels, defaultAction)
				channels = data;
				data = undefined;
			}
		}

		state = capitalize(state);
		channels = (channels || Object.keys(connectors)) as string[];

		for (let i = 0, len = channels.length; i < len; i++) {
			const connectorId = channels[i];
			if (connectors.hasOwnProperty(connectorId)) {
				const connector = connectors[connectorId];
				const proceed = connector.notify(this, 'on' + state, data) || false;

				if (!proceed) {
					shouldBeCalled = false;
				}
			} else {
				throw new Error(
					// tslint:disable:trailing-comma
					`The ${this.modName} module #${this.id} is not connected to connector ${connectorId}.
					Please make sure that your data is an object and not an array.`
					// tslint:enable:trailing-comma
				);
			}
		}

		// Execute default action unless a veto is provided
		if (shouldBeCalled) {
			if (typeof defaultAction === 'function') {
				defaultAction(data || {});
			}
		}
	}


	/**
	 * Attaches a connector (observer).
	 *
	 * @method attachConnector
	 * @param {Connector} connector
	 *      The connector to attach
	 */
	public attachConnector(connector : Connector) {
		this.connectors[connector.connectorId] = connector;
	}

	/**
	 * Detaches a connector (observer).
	 *
	 * @method detachConnector
	 * @param {Connector} connector The connector to detach
	 */
	public detachConnector(connector : Connector) {
		// tslint:disable-next-line:no-dynamic-delete
		delete this.connectors[connector.connectorId];
	}


	/**
	 * Decorates itself with the given skin.
	 *
	 * @method getDecoratedModule
	 * @param {String} module The name of the module
	 * @param {String} skin The name of the skin
	 * @return {Module} The decorated module
	 */
	public getDecoratedModule(skin : Skin<Module, Module>) : Module {
	/*
		* Sets the prototype object to the module.
		* So the "non-decorated" functions will be called on the module
		* without implementing the whole module interface.
		*/
		skin.prototype = this;
		skin.prototype.constructor = this.constructor;

		return new (skin as any as SkinConstructor<Module, Module>)(this);
	}

	/**
	 * Set a new hash state for this module.
	 * Updates the url hash.
	 *
	 * @param hashState The new hash state record.
	 */
	protected setModuleState(hashState : Record<string, any>) : void {
		if(!this.moduleStateChanged) {
			this.log.error('Module is not hash-capable');

			return;
		}
		saveStateToHash(this.hashKey, hashState);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/vendor/terrific/Module.ts